// Inbox global (Parte 14.4, M1 §8): agregação de tarefas, aprovações e exceções (REQ-WF-005/006). A insígnia e o
// painel do Topbar leem os mesmos hooks da página completa (`/me/inbox`), com contagem atualizada a cada 60s.
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@giyus/ui';
import { BellIcon, InboxIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useInboxCounters, useInboxItems } from '../modules/workflow/api/queries';
import { InboxItemCard } from '../modules/workflow/components/InboxItemCard';

export function InboxPanel() {
  const { t } = useTranslation(['shell', 'common']);
  const { data } = useInboxItems({ limit: 8 });
  const items = data?.items ?? [];
  return (
    <div className="flex flex-1 flex-col gap-2 p-4" data-testid="inbox-panel">
      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <InboxIcon className="size-8 text-muted-foreground" aria-hidden="true" />
          <p className="font-medium">{t('shell:inbox.empty')}</p>
          <p className="text-body text-muted-foreground">{t('shell:inbox.emptyHint')}</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <InboxItemCard key={`${item.kind}-${item.id}`} item={item} />
          ))}
        </ul>
      )}
      <Button asChild variant="outline" className="mt-2 min-h-(--control-h-sm)">
        <Link to="/me/inbox">{t('shell:inbox.viewAll')}</Link>
      </Button>
    </div>
  );
}

export interface InboxButtonProps {
  readonly showTrigger?: boolean;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
}

export function InboxButton({ showTrigger = true, open, onOpenChange }: InboxButtonProps = {}) {
  const { t } = useTranslation(['shell', 'common']);
  const { data } = useInboxCounters();
  const count = data?.total ?? 0;
  return (
    <Sheet
      {...(open !== undefined ? { open } : {})}
      {...(onOpenChange !== undefined ? { onOpenChange } : {})}
    >
      {showTrigger && (
        <SheetTrigger asChild>
          <Button
            variant="outline"
            className="relative gap-2 border-border px-3 min-h-(--control-h-sm)"
            aria-label={t('shell:inbox.open', { count })}
            data-testid="inbox-button"
          >
            <BellIcon aria-hidden="true" />
            <span className="hidden sm:inline">{t('shell:topbar.alerts')}</span>
            {count > 0 && (
              <span className="rounded-pill bg-brand px-1.5 text-caption font-bold text-primary">
                {count}
              </span>
            )}
          </Button>
        </SheetTrigger>
      )}
      <SheetContent side="right" closeLabel={t('common:a11y.closePanel')}>
        <SheetHeader>
          <SheetTitle>{t('shell:inbox.title')}</SheetTitle>
          <SheetDescription>{t('shell:inbox.emptyHint')}</SheetDescription>
        </SheetHeader>
        <InboxPanel />
      </SheetContent>
    </Sheet>
  );
}
